import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, Phone, MapPin, Hammer, Clock, AlertTriangle } from "lucide-react";
import ApexLoader from "@/components/ApexLoader";

const STATUS_STYLES = {
  new: { label: "NEW", cls: "bg-[#dc2626]/15 text-[#fca5a5] border-[#dc2626]/40" },
  routed: { label: "ROUTED", cls: "bg-white/5 text-zinc-200 border-white/10" },
  closed: { label: "CLOSED", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
};

export default function DashboardLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leads");
        setItems(data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div>
        <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">homeowner · find-a-pro</div>
        <h1 className="font-display text-4xl text-white tracking-tight">
          Routed <em className="silver-text not-italic">leads</em>.
        </h1>
        <p className="font-body text-sm text-zinc-400 mt-2">
          {items.length} homeowner request{items.length === 1 ? "" : "s"} routed to certified contractors.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-3 py-6" data-testid="dash-leads-loading">
          <ApexLoader inline size={18} />
          <span className="font-mono-apex text-xs uppercase tracking-[0.3em] text-zinc-500">loading</span>
        </div>
      )}
      {error && <div className="font-mono-apex text-xs text-[#dc2626]">{error}</div>}

      <div className="bg-[#121214] border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full font-mono-apex text-xs">
            <thead>
              <tr className="text-zinc-500 uppercase tracking-[0.2em] text-[10px] border-b border-white/5">
                <th className="text-left px-5 py-3 font-normal">Homeowner</th>
                <th className="text-left px-5 py-3 font-normal">ZIP</th>
                <th className="text-left px-5 py-3 font-normal">Damage</th>
                <th className="text-left px-5 py-3 font-normal">Routed to</th>
                <th className="text-left px-5 py-3 font-normal">Created</th>
                <th className="text-right px-5 py-3 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((lead) => {
                const status = STATUS_STYLES[lead.status] || STATUS_STYLES.new;
                return (
                  <tr key={lead.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-4">
                      <div className="text-white">{lead.name}</div>
                      <div className="text-zinc-500 mt-1 flex items-center gap-3 text-[10px]">
                        <span className="flex items-center gap-1"><Mail size={10} /> {lead.email}</span>
                        <span className="flex items-center gap-1"><Phone size={10} /> {lead.phone}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-zinc-300">
                      <span className="flex items-center gap-1.5"><MapPin size={11} /> {lead.zip}</span>
                    </td>
                    <td className="px-5 py-4 text-zinc-300">
                      <span className="flex items-center gap-1.5"><AlertTriangle size={11} className="text-[#dc2626]" /> {lead.damage_type}</span>
                    </td>
                    <td className="px-5 py-4 text-zinc-300">
                      <span className="flex items-center gap-1.5"><Hammer size={11} /> {lead.contractor_name || "Unassigned"}</span>
                    </td>
                    <td className="px-5 py-4 text-zinc-500 text-[10px]">
                      <span className="flex items-center gap-1.5"><Clock size={10} /> {new Date(lead.created_at).toLocaleString()}</span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span className={`px-2 py-1 border font-mono-apex text-[9px] uppercase tracking-[0.2em] ${status.cls}`}>
                        {status.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center font-mono-apex text-xs text-zinc-600">
                    No homeowner leads yet. Submit one via the landing page Find-a-Pro section to test.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
